import React, { useEffect, useState } from 'react';
import { geofenceAdminAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, MapPin, Edit, Trash2, Crosshair } from 'lucide-react';

const defaultForm = { name: '', latitude: '', longitude: '', radius: 100, company_id: '', enabled: true };

const Geofences = () => {
  const [zones, setZones] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [acting, setActing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([geofenceAdminAPI.list(), companiesAPI.getAll()]);
      setZones(g.data);
      setCompanies(c.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(defaultForm); setCreateOpen(true); };
  const openEdit = (z) => {
    setEditing(z);
    setForm({
      name: z.name, latitude: z.latitude, longitude: z.longitude,
      radius: z.radius, company_id: z.company_id || '', enabled: z.enabled !== false,
    });
    setCreateOpen(true);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm({ ...form, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) }); toast.success('Coordinates captured'); },
      (err) => toast.error(`GPS error: ${err.message}`),
    );
  };

  const save = async () => {
    if (!form.name || !form.company_id || form.latitude === '' || form.longitude === '') { toast.error('Name, company and coordinates required'); return; }
    setActing(true);
    try {
      const payload = {
        name: form.name,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius: parseFloat(form.radius),
        company_id: form.company_id,
        enabled: form.enabled,
      };
      if (editing) await geofenceAdminAPI.update(editing.id, payload);
      else await geofenceAdminAPI.create(payload);
      toast.success(editing ? 'Geofence updated' : 'Geofence created');
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this geofence?')) return;
    try { await geofenceAdminAPI.delete(id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleEnabled = async (z) => {
    try {
      await geofenceAdminAPI.update(z.id, { enabled: !(z.enabled !== false) });
      fetchAll();
    } catch (e) { toast.error('Toggle failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="geofences-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MapPin />Geofence Zones</h1>
          <p className="text-muted-foreground">Define office/site locations for attendance validation</p>
        </div>
        <Button onClick={openCreate} data-testid="add-geofence-btn"><Plus size={16} className="mr-2" />Add Geofence</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Zones</p><p className="text-2xl font-bold">{zones.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Enabled</p><p className="text-2xl font-bold text-emerald-600">{zones.filter(z => z.enabled !== false).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Disabled</p><p className="text-2xl font-bold text-amber-600">{zones.filter(z => z.enabled === false).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Companies Covered</p><p className="text-2xl font-bold">{new Set(zones.map(z => z.company_id)).size}</p></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Coordinates</TableHead>
            <TableHead>Radius (m)</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {zones.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No geofences defined</TableCell></TableRow>}
            {zones.map(z => (
              <TableRow key={z.id} data-testid={`geofence-row-${z.id}`}>
                <TableCell className="font-medium">{z.name}</TableCell>
                <TableCell className="text-xs">{companies.find(c => c.id === z.company_id)?.name || '—'}</TableCell>
                <TableCell className="font-mono text-xs">
                  {Number(z.latitude).toFixed(5)}, {Number(z.longitude).toFixed(5)}
                  <a href={`https://www.google.com/maps?q=${z.latitude},${z.longitude}`} target="_blank" rel="noreferrer" className="text-primary ml-2 hover:underline">map ↗</a>
                </TableCell>
                <TableCell>{z.radius}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={z.enabled !== false} onCheckedChange={() => toggleEnabled(z)} data-testid={`toggle-${z.id}`} />
                    <Badge variant={z.enabled !== false ? 'default' : 'outline'}>{z.enabled !== false ? 'Active' : 'Off'}</Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(z)} data-testid={`edit-${z.id}`}><Edit size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(z.id)} data-testid={`del-${z.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Geofence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="HQ Office, Site B..." data-testid="geofence-name" /></div>
            <div>
              <Label>Company *</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="geofence-company"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.country})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Latitude *</Label><Input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="25.276987" data-testid="geofence-lat" /></div>
              <div><Label>Longitude *</Label><Input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="55.296249" data-testid="geofence-lng" /></div>
            </div>
            <Button type="button" variant="outline" onClick={useMyLocation} className="w-full" data-testid="use-gps-btn"><Crosshair size={14} className="mr-2" />Use my current GPS location</Button>
            <div><Label>Radius (meters) *</Label><Input type="number" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} data-testid="geofence-radius" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <Label className="text-sm">Enable enforcement (block punches outside zone)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={acting} data-testid="save-geofence-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Geofences;
